import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { TrendingUp, Users } from "lucide-react";

// Growth Trend — reuses EmailTrendChart's hand-rolled, dependency-free
// sparkline SVG technique (small multi-metric grid, one independently-
// scaled line per metric), rather than that component directly: its
// METRICS list is hardcoded to the acquisition-funnel metrics (Delivered/
// LP Views/Quote Starts/Quote Completes/High Intent), which have no
// meaning on this page. A local copy with Audience-appropriate metrics
// (New Records/Unsubscribes/Net Growth) is the smallest compatible
// sibling, kept private to this file rather than a new shared module.
const TREND_METRICS = [
  { key: "newRecords", label: "New Records", color: "#10b981" },
  { key: "unsubscribes", label: "Unsubscribes", color: "#f59e0b" },
  { key: "netGrowth", label: "Net Growth", color: "#6366f1" },
];

const TREND_CHART_WIDTH = 160;
const TREND_CHART_HEIGHT = 48;
const TREND_PADDING = 4;

function TrendSparkline({ points, color }) {
  if (!points || points.length === 0) {
    return <div className="flex h-[48px] items-center justify-center text-xs text-slate-500">No data</div>;
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const xStep = (TREND_CHART_WIDTH - TREND_PADDING * 2) / Math.max(points.length - 1, 1);

  const path = points
    .map((v, i) => {
      const x = TREND_PADDING + i * xStep;
      const y = TREND_PADDING + (TREND_CHART_HEIGHT - TREND_PADDING * 2) * (1 - (v - min) / range);
      return `${i === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      width={TREND_CHART_WIDTH}
      height={TREND_CHART_HEIGHT}
      viewBox={`0 0 ${TREND_CHART_WIDTH} ${TREND_CHART_HEIGHT}`}
      data-testid="audience-trend-sparkline"
    >
      <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// DASH-EMAIL-010: previously `return null` when trend was empty — that was
// unreachable in practice, since the adapter always fabricated points. Now
// that no data source (real or mock) is ever consulted for this section,
// this always renders the explicit unavailable state. The outer Card keeps
// the SAME data-testid ("audience-growth-trend-card") it always has —
// DASH-EMAIL-P1-IMPL-008 does not touch this section at all (see this
// file's own header comment): it was already honestly disclosed before
// this checkpoint, unlike everything else this view used to render.
function AudienceGrowthTrendCard({ trend }) {
  const hasTrend = trend && trend.length > 0;

  return (
    <Card className="glass border-white/10" data-testid="audience-growth-trend-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <TrendingUp className={`h-5 w-5 ${hasTrend ? "text-indigo-400" : "text-slate-500"}`} />
          Growth Trend
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasTrend ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {TREND_METRICS.map((m) => {
              const points = trend.map((t) => t[m.key] || 0);
              const latest = points[points.length - 1];
              return (
                <div key={m.key} className="space-y-1" data-testid={`audience-trend-metric-${m.key}`}>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-300/70">{m.label}</p>
                  <TrendSparkline points={points} color={m.color} />
                  <p className="text-sm font-semibold text-slate-100">{latest.toLocaleString()}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-400" data-testid="audience-growth-trend-card-unavailable">
            Trend data is unavailable — this time series is not yet backed by real event-time/send-cohort data
            (EMAIL-ANALYTICS-SPEC-001 §19).
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * EmailAudienceView — DASH-EMAIL-004.
 *
 * DASH-EMAIL-P1-IMPL-008: retires this view's entire prior operational
 * content — the Size & Movement KPI row (Recipient Records/Net Growth/New
 * Records/Unsubscribe Rate), the Lifecycle Distribution funnel, and the
 * four Segment Breakdown tables (Lead Source/Acquisition Channel/
 * Geography/Lifecycle State). DASH-EMAIL-P1-CHAR-007 found this entire
 * "audience segmentation" concept was a 100% hardcoded 20-row fixture
 * (emailAudienceMockAdapter.js's AUDIENCE_SEGMENT_ROWS) with no
 * authoritative backend source of any kind — unlike Performance, there is
 * no adjacent real contract to wire this content to; only an honest
 * disclosure is possible. Deliberately does NOT substitute
 * sendgrid_hygiene_recipient_state — that is a different, RECIPIENT-grain
 * operational domain (Deliverability's own hygiene tracking), not a stand-
 * in for the invented Audience segmentation model.
 *
 * No longer calls useEmailAudienceData (which called
 * emailAudienceMockAdapter.js's getMockEmailAudienceData) — both are left
 * in place, unconsumed, as documented follow-up debt rather than deleted
 * (see this checkpoint's own completion report). Its own contextual
 * filters (Lead Source/Acquisition Channel/Geography/Lifecycle State) no
 * longer render at all — see emailViewConfig.js's `audience.contextual: []`
 * comment.
 *
 * DASH-EMAIL-P1-IMPL-008A: the shared Period/Environment controls (and
 * Apply/Advanced) no longer render for this view either — see
 * EmailFilterBar.jsx's own EMAIL_FILTER_BAR_FIELDS.unavailable comment —
 * so there is no interactive filter-bar control left at all implying this
 * view has authoritative, filterable data. Only the Email: view selector
 * (navigation, not a query filter) remains visible above this content.
 *
 * Growth Trend is the one section left untouched: it was already honestly
 * disclosed as unavailable before this checkpoint (no fabricated content
 * to remove), so it is preserved unchanged below the new disclosure.
 */
export default function EmailAudienceView() {
  return (
    <div className="space-y-6" data-testid="email-audience-view">
      <Card className="glass border-white/10" data-testid="audience-unavailable">
        <CardContent className="p-8 text-center text-slate-400">
          <Users className="mx-auto h-8 w-8 text-slate-500" aria-hidden="true" />
          <p className="mt-3 text-lg text-slate-300">Audience &amp; Acquisition reporting is not yet available</p>
          <p className="mt-2 text-sm">
            MissionAssist does not yet have an authoritative source for audience segmentation (recipient
            records, lead source, acquisition channel, geography, or lifecycle state). This section will
            populate once that data source exists.
          </p>
        </CardContent>
      </Card>

      <AudienceGrowthTrendCard trend={[]} />
    </div>
  );
}
